import { EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { pageAndOrderParamsAtom } from '@/atoms/general';
import { isMappedMetricPendingAtom, isMetricMappedAtom } from '@/atoms/metric/metricFilterMapping';
import { playerIdAtom } from '@/atoms/player/player';
import { selectedGameIdForVideosAtom } from '@/atoms/video';
import { EVENT_MARKERS_LIMIT } from '@/consts/visualisations';
import { playerMetricEventsUrl, visEventsUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, gameEventParams, hasEmptyInFilters } from '@/utils/api';
import {
  convertEventDataIntoDescriptions,
  convertMarkerDataIntoMarkers,
  convertMarkerDataIntoDescriptions,
  convertMarkerDataToShotMapMarkers,
  processEventDataAtom,
} from '@/utils/atoms/eventData';
import { createUnwrappedPitchEventsAtom } from '@/utils/atoms/vis';
import { fetchClientAtom } from '../client';

export const playerEventMetricFiltersDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: playerEventMetricFiltersAtom } = playerEventMetricFiltersDebounceObject;

const playerRawEventMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventMarkersForMetric',
    get(filtersAtom).metric,
    get(playerIdAtom),
    get(playerEventMetricFiltersAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, playerId, filterParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !playerId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    const url = visEventsUrl(
      'player',
      playerId,
      metricKey,
      'event-markers',
      { ...filterParams, limit: EVENT_MARKERS_LIMIT },
      isMetricMapped,
    );
    if (!url) return [];
    return (await fetch(url)) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

const playerEventMarkersDataAtom = atom(async get => {
  const rawMarkerData = await get(playerRawEventMarkersAtom);
  return {
    markers: convertMarkerDataIntoMarkers(rawMarkerData.data),
    descriptions: convertMarkerDataIntoDescriptions(rawMarkerData.data),
  };
});

export const playerEventMarkersForMetricAtom = atom(async get => {
  const { markers } = await get(playerEventMarkersDataAtom);
  return markers;
});

export const unwrappedPlayerEventMarkersForMetricAtom = createUnwrappedPitchEventsAtom(playerEventMarkersForMetricAtom);

const playerRawEventsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventsForMetric',
    get(filtersAtom).metric,
    get(playerIdAtom),
    get(playerEventMetricFiltersAtom),
    get(pageAndOrderParamsAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, playerId, filterParams, pageAndOrderParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !playerId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    const url = visEventsUrl(
      'player',
      playerId,
      metricKey,
      'events',
      { ...pageAndOrderParams, ...filterParams },
      isMetricMapped,
    );
    if (!url) return [];

    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});
export const playerEventsForMetricAtom = atom(async get => processEventDataAtom(await get(playerRawEventsAtom)));

const playerRawEventsCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventsForMetricCount',
    get(filtersAtom).metric,
    get(playerIdAtom),
    get(playerEventMetricFiltersAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, playerId, filterParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !playerId || !metricKey || hasEmptyInFilters(filterParams)) return 0;
    const url = visEventsUrl('player', playerId, metricKey, 'events/count', { ...filterParams }, isMetricMapped);
    if (!url) return 0;
    return (await fetch(url)) as Promise<number>;
  };

  return { queryKey, queryFn };
});
export const playerEventsForMetricCountAtom = atom(async get => (await get(playerRawEventsCountAtom)).data);

const playerRawEventsForMetricInGameAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'playerEventsForMetricInGame',
    get(filtersAtom).metric,
    get(playerIdAtom),
    get(selectedGameIdForVideosAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, metricKey, playerId, gameId] }: { queryKey: typeof queryKey }) => {
    const url = playerMetricEventsUrl(playerId, metricKey, gameEventParams(gameId));
    if (!url || !gameId) return [];

    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const playerEventMarkersDescriptionsAtom = atom(async get => {
  const { descriptions } = await get(playerEventMarkersDataAtom);
  return descriptions;
});

export const playerGameEventDescriptionsAtom = atom(async get => {
  const playerRawEventsForMetricInGame = await get(playerRawEventsForMetricInGameAtom);
  return convertEventDataIntoDescriptions(playerRawEventsForMetricInGame.data);
});

export const playerEventDataDescriptionsAtom = atom(async get => {
  const playerRawEvents = await get(playerRawEventsAtom);
  return convertEventDataIntoDescriptions(playerRawEvents.data);
});

const rawPlayerShotMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerShotMarkers', get(playerIdAtom), get(playerEventMetricFiltersAtom)] as const;

  const queryFn = async ({ queryKey: [, playerId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!playerId || hasEmptyInFilters(filterParams)) return [];

    return (await fetch(
      `/event-markers${convertFilterParamsToString({
        ...filterParams,
        limit: EVENT_MARKERS_LIMIT,
      })}&eq[event.player_id]=${playerId}&eq[event.type]=shot&neq[event.period]=5`,
    )) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

export const playerShotMarkersAtom = atom(async get => {
  const playerShotMarkers = await get(rawPlayerShotMarkersAtom);
  return convertMarkerDataToShotMapMarkers(playerShotMarkers.data);
});

export const unwrappedPlayerShotMarkersAtom = unwrap(playerShotMarkersAtom, prev => prev || []);
